import { formatCurrency } from "@/lib/currency";
/**
 * Command Bar — AI answer layer.
 *
 * The UI talks ONLY to `CommandAiProvider`. Today the implementation is the
 * existing deterministic rule-based intelligence (Operations snapshot +
 * RuleBasedOperationsIntelligenceProvider). Swapping in an LLM later means
 * providing a different `CommandAiProvider` — no UI changes required.
 */
import {
  loadOperationsSnapshot,
  RuleBasedOperationsIntelligenceProvider,
  type OperationsSnapshot,
} from "@/services/operationsCentre";

export interface CommandAiSource {
  label: string;
  to?: string;
}

export interface CommandAiAnswer {
  /** Provider identifier, surfaced in the UI footer for transparency. */
  provider: string;
  headline: string;
  detail: string;
  bullets: string[];
  sources: CommandAiSource[];
  confidence: "high" | "medium" | "low";
}

export interface CommandAiContext {
  companyId: string;
  isDriver: boolean;
}

export interface CommandAiProvider {
  id: string;
  label: string;
  answer(question: string, ctx: CommandAiContext): Promise<CommandAiAnswer>;
  /** Suggested prompts shown before the user types. */
  suggestions(ctx: CommandAiContext): string[];
}

const pluralise = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

type Intent =
  | "late"
  | "unassigned"
  | "drivers"
  | "invoices"
  | "appraisals"
  | "customers"
  | "vehicles"
  | "today"
  | "attention"
  | "overview";

function classify(question: string): Intent {
  const q = question.toLowerCase();
  if (/(late|delay|overdue|behind|sla|risk)/.test(q)) return "late";
  if (/(unassigned|no driver|needs? a driver|allocate|assign)/.test(q)) return "unassigned";
  if (/(driver|who is|available|working)/.test(q)) return "drivers";
  if (/(invoice|billing|unpaid|revenue|paid|money|owe)/.test(q)) return "invoices";
  if (/(appraisal|poc|pod|photo|condition|damage)/.test(q)) return "appraisals";
  if (/(customer|client|account)/.test(q)) return "customers";
  if (/(vehicle|van|fleet|check|defect)/.test(q)) return "vehicles";
  if (/(today|schedule|due|tomorrow)/.test(q)) return "today";
  if (/(attention|problem|issue|wrong|urgent)/.test(q)) return "attention";
  return "overview";
}

function buildAnswer(
  intent: Intent,
  snapshot: OperationsSnapshot,
  insights: { title: string; detail: string; recommendation?: string }[],
  ctx: CommandAiContext,
): CommandAiAnswer {
  const base = {
    provider: "rules-v1",
    confidence: "high" as const,
    sources: [] as CommandAiSource[],
  };
  const active = snapshot.jobs.filter((j) => j.lane !== "completed");
  const opsSource: CommandAiSource = { label: "Operations Command Centre", to: "/operations" };

  switch (intent) {
    case "late": {
      const late = active.filter((j) => j.minutesLate > 0).sort((a, b) => b.minutesLate - a.minutesLate);
      return {
        ...base,
        headline: late.length
          ? `${pluralise(late.length, "job")} running late`
          : "Nothing is running late",
        detail: late.length
          ? `The worst is ${late[0].reference}, ${late[0].minutesLate} minutes behind.`
          : "Every active job is currently within its expected window.",
        bullets: late.slice(0, 5).map((j) => `${j.reference} — ${j.minutesLate}m late · ${j.driverName ?? "unassigned"}`),
        sources: [opsSource],
      };
    }
    case "unassigned": {
      const unassigned = active.filter((j) => !j.driverId);
      const available = snapshot.drivers.filter((d) => d.availability === "available");
      return {
        ...base,
        headline: unassigned.length
          ? `${pluralise(unassigned.length, "job")} without a driver`
          : "Every active job has a driver",
        detail: unassigned.length
          ? `${pluralise(available.length, "driver")} currently available to take them.`
          : "No allocation action needed right now.",
        bullets: unassigned.slice(0, 5).map((j) => `${j.reference} — ${j.collection} → ${j.delivery}`),
        sources: [opsSource],
      };
    }
    case "drivers": {
      const working = snapshot.drivers.filter((d) => d.availability === "working");
      const available = snapshot.drivers.filter((d) => d.availability === "available");
      return {
        ...base,
        headline: `${pluralise(working.length, "driver")} on a job, ${available.length} available`,
        detail: `${snapshot.drivers.length} drivers on the account.`,
        bullets: snapshot.drivers
          .slice(0, 6)
          .map((d) => `${d.name} — ${d.availability}${d.currentJob ? ` (${d.currentJob.reference})` : ""}`),
        sources: [opsSource, { label: "Driver Management", to: "/driver-management" }],
      };
    }
    case "invoices": {
      if (ctx.isDriver) {
        return {
          ...base,
          headline: "Financial data isn't available on a driver account",
          detail: "Invoice and revenue questions are restricted to admin users.",
          bullets: [],
          sources: [],
          confidence: "high",
        };
      }
      const uninvoiced = snapshot.jobs.filter((j) => j.lane === "completed" && !j.invoiceGenerated);
      const unpaid = snapshot.jobs.filter((j) => j.invoiceGenerated && j.amountDue > 0);
      const outstanding = unpaid.reduce((sum, j) => sum + j.amountDue, 0);
      return {
        ...base,
        headline: `${pluralise(uninvoiced.length, "job")} awaiting an invoice`,
        detail: `${formatCurrency(outstanding.toFixed(2))} outstanding across ${pluralise(unpaid.length, "invoice")}.`,
        bullets: [
          ...uninvoiced.slice(0, 4).map((j) => `${j.reference} — completed, not invoiced`),
          ...unpaid.slice(0, 3).map((j) => `${j.invoiceNumber ?? j.reference} — ${formatCurrency(j.amountDue.toFixed(2))} due`),
        ],
        sources: [{ label: "Ready to Invoice", to: "/ready-to-invoice" }, { label: "Invoicing", to: "/invoicing" }],
      };
    }
    case "appraisals": {
      const missing = snapshot.jobs.filter(
        (j) => j.lane !== "scheduled" && (!j.collectionAppraisalId || (j.deliveredAt && !j.deliveryAppraisalId)),
      );
      return {
        ...base,
        headline: missing.length
          ? `${pluralise(missing.length, "job")} missing an appraisal`
          : "All appraisals are captured",
        detail: missing.length
          ? "Missing PoC/PoD evidence delays invoicing and weakens damage disputes."
          : "Collection and delivery evidence is complete for active work.",
        bullets: missing.slice(0, 5).map((j) => `${j.reference} — ${j.collectionAppraisalId ? "delivery" : "collection"} appraisal outstanding`),
        sources: [opsSource],
      };
    }
    case "customers": {
      if (ctx.isDriver) {
        return {
          ...base,
          headline: "Customer data isn't available on a driver account",
          detail: "Ask an administrator for customer-level information.",
          bullets: [],
          sources: [],
        };
      }
      return {
        ...base,
        headline: snapshot.customerAlerts.length
          ? `${pluralise(snapshot.customerAlerts.length, "customer")} need attention`
          : "No customer issues detected",
        detail: snapshot.customerAlerts.length
          ? "These accounts have active operational or billing signals."
          : "Nothing flagged across active jobs.",
        bullets: snapshot.customerAlerts.slice(0, 5).map((c) => `${c.name} — ${c.reason}: ${c.detail}`),
        sources: [{ label: "Customers", to: "/customers" }],
      };
    }
    case "vehicles": {
      const warned = snapshot.vehicles.filter((v) => v.warnings.length || v.hasDefects);
      return {
        ...base,
        headline: warned.length
          ? `${pluralise(warned.length, "vehicle")} with warnings`
          : "No vehicle warnings",
        detail: `${snapshot.vehicles.length} vehicles on the fleet.`,
        bullets: warned.slice(0, 5).map((v) => `${v.registration} — ${(v.warnings[0] ?? v.defectNotes) || "defect reported"}`),
        sources: [{ label: "Vehicles", to: "/vehicles" }, { label: "Daily Checks", to: "/vehicle-daily-checks" }],
      };
    }
    case "today": {
      const today = snapshot.jobs.filter((j) => j.isToday);
      const done = today.filter((j) => j.lane === "completed" || j.lane === "delivered");
      return {
        ...base,
        headline: `${pluralise(today.length, "job")} today`,
        detail: `${done.length} delivered or completed, ${today.length - done.length} still in progress.`,
        bullets: today.slice(0, 6).map((j) => `${j.reference} — ${j.lane.replace(/_/g, " ")} · ${j.driverName ?? "unassigned"}`),
        sources: [opsSource, { label: "Schedule", to: "/schedule" }],
      };
    }
    case "attention":
    default: {
      const critical = snapshot.attention.filter((a) => a.severity === "critical" || a.severity === "high");
      return {
        ...base,
        headline: critical.length
          ? `${pluralise(critical.length, "item")} needs your attention`
          : `${pluralise(active.length, "active job")} · operation looks healthy`,
        detail:
          insights[0]?.detail ??
          (critical.length
            ? "Highest severity items first."
            : "No critical or high severity signals across today's operation."),
        bullets: [
          ...critical.slice(0, 4).map((a) => `${a.title} — ${a.detail}`),
          ...insights.slice(0, 2).map((i) => `${i.title}${i.recommendation ? ` — ${i.recommendation}` : ""}`),
        ],
        sources: [opsSource],
      };
    }
  }
}

/**
 * Deterministic provider built on the existing rule engines. Replaceable.
 */
export const RuleBasedCommandAiProvider: CommandAiProvider = {
  id: "rules-v1",
  label: "Rule-based intelligence",
  suggestions(ctx) {
    const shared = [
      "What needs my attention right now?",
      "Which jobs are running late?",
      "What is happening today?",
      "Which vehicles have warnings?",
    ];
    if (ctx.isDriver) return shared;
    return [...shared, "Which jobs still need invoicing?", "Which jobs have no driver assigned?"];
  },
  async answer(question, ctx) {
    const snapshot = await loadOperationsSnapshot(ctx.companyId);
    const insights = await RuleBasedOperationsIntelligenceProvider.analyse(snapshot);
    return buildAnswer(classify(question), snapshot, insights, ctx);
  },
};

/** Single seam for swapping in an LLM-backed provider later. */
export function getCommandAiProvider(): CommandAiProvider {
  return RuleBasedCommandAiProvider;
}
